import { WarningAmber } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import type { FC } from "react";

interface DeleteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  itemName: string;
  itemType?: string;
  message?: string;
  isDeleting?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

export const DeleteModal: FC<DeleteModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  itemName,
  itemType = "item",
  message,
  isDeleting = false,
  confirmButtonText = "Delete",
  cancelButtonText = "Cancel",
}) => {
  const defaultTitle = `Delete ${itemType}`;
  const defaultMessage = `Are you sure you want to delete "${itemName}"? This action cannot be undone.`;

  return (
    <Dialog
      open={open}
      onClose={isDeleting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: (theme) => theme.spacing(1.5),
          boxShadow: 8,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          pt: 3,
          px: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: "error.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& .MuiSvgIcon-root": {
              color: "error.main",
              fontSize: 28,
            },
          }}
        >
          <WarningAmber />
        </Box>
        <Typography variant="h6">{title || defaultTitle}</Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          {message || defaultMessage}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
        <Button onClick={onClose} disabled={isDeleting} color="inherit">
          {cancelButtonText}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {isDeleting ? "Deleting..." : confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
